import { Box, Stack, TextField } from "@mui/material";
import React, { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";

import {
  AutoCompleteUncontrolled,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
} from "@4uhub/lib4uhub";
import { FilterParamEntry, IMacroFilterProps } from "./models";

const translationPath = "chatBot.page.logs.examSchedulingAttachmentLog.";

const MacroFilter: React.FC<IMacroFilterProps> = ({
  onFilterParams,
  filterParams,
}) => {
  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);

  const { t } = useTranslation();

  const filterHandler = () => {
    onFilterParams(filter);
  };

  const [resetDateRange, setResetDateRange] = useState<boolean>(false);
  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
    setResetDateRange((prev) => !prev);
  };

  const [enableFields, setEnableFields] = useState<boolean>(true);

  const handleStartDate = (date: Date | null) => {
    setFilter({ dateStart: { ...filterParams.dateStart, value: date } });
  };

  const handleEndDate = (date: Date | null) => {
    if (filterParams.dateEnd)
      setFilter({ dateEnd: { ...filterParams.dateEnd, value: date } });
  };

  const [selectedStatus, setSelectedStatus] = useState<ISelectType | null>(
    null,
  );

  const statuses = [
    {
      id: "1",
      name: t(translationPath + "sync"),
    },
    {
      id: "2",
      name: t(translationPath + "not_sync"),
    },
  ];

  const handleSelectStatus = useCallback(
    (value: ISelectType | null) => {
      const statusValue = value ? value.id === "1" : undefined;
      setFilter({
        status: { ...filterParams.status, value: statusValue },
      });
      setSelectedStatus(value);
    },
    [setFilter, filterParams],
  );

  return (
    <Stack flexDirection="row" gap={2} alignItems="center">
      <DateRangeSelect
        setEndDate={handleEndDate}
        setStartDate={handleStartDate}
        setEnableFields={setEnableFields}
        resetToToday={resetDateRange}
      />

      {(Object.entries(filter) as FilterParamEntry[]).map(([key, v]) => {
        const isDate = key === "dateStart" || key === "dateEnd";
        const isStatus = key === "status";

        return (
          <React.Fragment key={key}>
            {isDate && (
              <>
                {typeof v?.value !== "string" &&
                  typeof v?.value !== "boolean" && (
                    <Box sx={{ flex: "0 0 160px" }}>
                      <DatePickerUncontrolled
                        value={v?.value}
                        onChange={(e) =>
                          setFilter({ [key]: { ...v, value: e } })
                        }
                        label={t(translationPath + v?.label) || "Date"}
                        isClearable={false}
                        disable={enableFields}
                      />
                    </Box>
                  )}
              </>
            )}

            {isStatus && (
              <AutoCompleteUncontrolled
                label={t(translationPath + v?.label)}
                onValueSelected={handleSelectStatus}
                value={selectedStatus}
                options={statuses}
                size="small"
                fullWidth
              />
            )}

            {!isDate && !isStatus && v?.label && (
              <TextField
                value={v?.value}
                size="small"
                label={t(translationPath + v?.label)}
                fullWidth
                onChange={(e) => {
                  setFilter({ [key]: { ...v, value: e.target.value } });
                }}
              />
            )}
          </React.Fragment>
        );
      })}

      <FiltersActions
        onFilter={filterHandler}
        onClean={cleanHandler}
        showCleanBtn={showCleanBtn}
      />
    </Stack>
  );
};

export default memo(MacroFilter);
